import { useQueryClient } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { useAIAssistantCreateConversationMutation } from '../../../../queries/AI/useAIAssistantCreateConversationMutation';
import {
	getConversationChatMessageQueryKey,
	useAIConversationChatMessageQuery,
} from '../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import {
	invalidateAllAIConversationFilterQueries,
	useAIConversationFilterQuery,
} from '../../../../queries/AIConversation/useAIConversationFilterQuery';
import WorkingLionImageUrl from '../../../assets/aiAssistantThinking.png';
import { Avatar2 } from '../../../components/Avatar2';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { SparklesIcon } from '../../../components/svgs/icons/SparklesIcon';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import {
	AIChatAssistantMessage,
	removeErrorMessageFromConversationCache,
	useAIAssistantStreamingMutation,
} from '../../../containers/templates/AssistantChat';
import { reactQueryClient } from '../../../ReactQueryProvider';
import { Button } from '../../Button';
import { Dialog, DialogBackdrop, DialogBody, DialogHeader, DialogPopup, DialogPortal, DialogTitle } from '../../Dialog';
import { Column, Row } from '../../PageLayout';
import { AIConversationMessage } from '../AIConversationMessage';
import { ChatConversation, ChatConversationContent, ChatConversationScrollButton } from '../ChatConversation';
import { ChatInput } from '../ChatInput';
import { s } from './styles';

interface AIAssistantDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	impersonationContext?: Api.IImpersonationContext;
	assistantName: string;
	conversationType: Api.AIConversationType;
	title?: string;
	initialMessage?: string;
	placeholder?: string;
	agentTemplateId?: string;
	customInitialAction?: Api.AIAction;
	dismissOnOutsideClick?: boolean;
}

const DEFAULT_TITLE = 'Ask Lev';
const DEFAULT_INITIAL_MESSAGE =
	"Hey! I'm here to help you with any questions you have. Feel free to ask me anything about your account, features, or general guidance.";
const DEFAULT_PLACEHOLDER = 'Ask me anything...';

const AIAssistantDialogLayout = ({ children, title }: { children: React.ReactNode; title: string }) => {
	return (
		<>
			<DialogHeader styles={[s.header]}>
				<div className={css(s.headerStart)}>
					<Avatar2 imgSrc={WorkingLionImageUrl} fallbackText='Leo Avatar' styleDeclaration={s.leoAvatar} />
					<SparklesIcon className={css(s.sparklesIcon)} />
					<DialogTitle styles={[s.headerTitle]}>{title}</DialogTitle>
				</div>
			</DialogHeader>
			<DialogBody styles={[s.body]}>{children}</DialogBody>
		</>
	);
};

const useAIAssistantConversation = (
	isOpen: boolean,
	assistantName: string,
	conversationType: Api.AIConversationType,
	impersonationContext?: Api.IImpersonationContext,
	agentTemplateId?: string
) => {
	const logger = useEventLogging('useAIAssistantConversation');

	// Try to find an existing conversation of this type
	// Note: When agentTemplateId is provided (testing mode), we ignore the result and always create new
	const aiConversationFilterQuery = useAIConversationFilterQuery({
		filter: {
			criteria: [
				{
					property: Api.AIConversationFilterProperty.ConversationType,
					value: conversationType,
				},
			],
		},
		impersonationContext,
	});

	// If no conversation exists, create one
	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			logger.logApiError(`useAIAssistantConversation-error`, error);
			toast.error('Failed to create conversation');
		},
	});

	// Get the conversation ID (from existing or newly created)
	const conversationId = React.useMemo(() => {
		// When testing a specific agent template, only use the newly created conversation
		if (agentTemplateId) {
			return aiAssistantCreateConversationMutation.data?.id;
		}
		const existingConversation = aiConversationFilterQuery.data?.[0];
		if (existingConversation) {
			return existingConversation.id;
		}
		return aiAssistantCreateConversationMutation.data?.id;
	}, [aiConversationFilterQuery.data, aiAssistantCreateConversationMutation.data, agentTemplateId]);

	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(conversationId),
		id: conversationId,
		impersonationContext,
		staleTime: Infinity,
	});

	// Create conversation if needed
	React.useEffect(() => {
		// When agentTemplateId is provided, always create a new conversation (ignore filter results)
		const shouldCreateNew = agentTemplateId
			? aiAssistantCreateConversationMutation.isIdle && !aiAssistantCreateConversationMutation.data
			: !aiConversationFilterQuery.isLoading &&
				!aiConversationFilterQuery.data?.[0] &&
				aiAssistantCreateConversationMutation.isIdle &&
				!aiAssistantCreateConversationMutation.data;

		if (isOpen && shouldCreateNew) {
			aiAssistantCreateConversationMutation.mutate({
				assistantName,
				impersonationContext,
				agentTemplateId,
			});
		}
	}, [
		aiConversationFilterQuery.isLoading,
		aiConversationFilterQuery.data,
		aiAssistantCreateConversationMutation,
		isOpen,
		impersonationContext,
		assistantName,
		conversationType,
		agentTemplateId,
	]);

	return {
		conversationId,
		aiConversationChatMessageQuery,
		isLoadingConversation:
			(!agentTemplateId && aiConversationFilterQuery.isLoading) || aiAssistantCreateConversationMutation.isPending,
	};
};

export function clearConversationCache(conversationId: string, impersonationContext?: Api.IImpersonationContext) {
	const conversationQueryKey = getConversationChatMessageQueryKey({
		id: conversationId,
		impersonationContext,
	});
	const messages: Api.AIChatMessage[] = reactQueryClient.getQueryData(conversationQueryKey) ?? [];
	if (messages?.length) {
		reactQueryClient.setQueryData(conversationQueryKey, []);
	}
}

const AIAssistantDialogContent = ({
	open,
	impersonationContext,
	assistantName,
	conversationType,
	title = DEFAULT_TITLE,
	initialMessage = DEFAULT_INITIAL_MESSAGE,
	placeholder = DEFAULT_PLACEHOLDER,
	agentTemplateId,
	customInitialAction,
}: AIAssistantDialogProps) => {
	const id = React.useId();
	const queryClient = useQueryClient();
	const logger = useEventLogging('AIAssistantDialog');
	const errorMessages = useErrorMessages();

	const { mutate: createUniversalAssistantConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: data => {
			if (
				data.conversationType === Api.AIConversationType.AccountOnboarding &&
				!aiAssistantStreamingMutation.isLoading
			) {
				aiAssistantStreamingMutation.mutate({
					conversationId: data.id,
					chatRequest: {
						_type: 'AIActionRequest',
						action: customInitialAction || {
							_type: 'ContentSettingsIntakeAction',
						},
					} as Api.AIActionRequest,
					impersonationContext,
				});
			}
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SiteWide-Assistant-Error`, error);
		},
	});

	const { conversationId, aiConversationChatMessageQuery, isLoadingConversation } = useAIAssistantConversation(
		open,
		assistantName,
		conversationType,
		impersonationContext,
		agentTemplateId
	);

	React.useEffect(() => {
		// Skip creating universal assistant conversation when testing a specific agent template
		// as the useAIAssistantConversation hook handles that case
		if (!agentTemplateId) {
			createUniversalAssistantConversation({ assistantName, impersonationContext });
		}
		return () => {
			clearConversationCache(conversationId, impersonationContext);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [impersonationContext, agentTemplateId]);

	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName,
	});

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		if (!conversationId) {
			return;
		}
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};

	const handlePromptSubmit = async (prompt: string) => {
		if (!conversationId) {
			toast.error('Conversation not ready. Please try again.');
			return;
		}

		try {
			removeErrorMessageFromConversationCache(conversationId, impersonationContext);

			// Add user message to cache
			queryClient.setQueryData(
				getConversationChatMessageQueryKey({
					id: conversationId,
					impersonationContext,
				}),
				(prevMessages: Api.AIChatMessage[]) => [
					...(prevMessages ?? []),
					{
						role: Api.AIChatRole.User,
						text: prompt,
						id: null,
					},
				]
			);

			// Send to API
			const chatRequest: Api.AIMessageRequest = {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text: prompt,
						id: null,
					},
				],
			};

			aiAssistantStreamingMutation.mutate({
				conversationId,
				chatRequest,
				impersonationContext,
			});
		} catch (error) {
			logger.logApiError('AIAssistantDialog-submit-error', error as Api.IOperationResultNoValue);
			toast.error('Failed to send message. Please try again.');
		}
	};

	if (isLoadingConversation || !conversationId) {
		return (
			<AIAssistantDialogLayout title={title}>
				<Row fullHeight center>
					<LoadingSpinner />
				</Row>
			</AIAssistantDialogLayout>
		);
	}

	return (
		<AIAssistantDialogLayout title={title}>
			<ChatConversation styles={[s.conversation]}>
				<ChatConversationContent styles={[s.conversationContent]}>
					<div className={css(s.spacer)} />
					<AIChatAssistantMessage>
						<span dangerouslySetInnerHTML={{ __html: initialMessage }} />
					</AIChatAssistantMessage>
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-assistant-message-${message.id ?? i}`}
								message={message}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId}
								impersonationContext={impersonationContext}
								showMessageToolbar={false}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading && (
						<AIChatAssistantMessage>
							<LoadingSpinner type='tiny' />
						</AIChatAssistantMessage>
					)}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<ChatInput
					streamingInProgress={aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming}
					placeholder={placeholder}
					onSubmit={handlePromptSubmit}
					supportsImages={false}
				/>
			</div>
		</AIAssistantDialogLayout>
	);
};

const AIAssistantDialogErrorBoundary = ({ children }: { children: React.ReactNode }) => {
	const logger = useEventLogging('AIAssistantDialogErrorBoundary');

	return (
		<ErrorBoundary
			fallback={
				<Column fullHeight center styles={[s.errorBoundaryDisplay]}>
					<WarningIcon />
					<strong className={css(s.errorMessage)}>Error occurred loading assistant</strong>
					<Button variant='secondary' onClick={() => window.location.reload()}>
						Reload Page
					</Button>
				</Column>
			}
			onError={(error: Error) => {
				logger.logApiError('AIAssistantDialogErrorBoundary-Error', error as Api.IOperationResultNoValue);
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export const AIAssistantDialog = (props: AIAssistantDialogProps) => {
	const handleOpenChange = React.useCallback(
		(open: boolean) => {
			props.onOpenChange(open);
		},
		[props]
	);

	return (
		<Dialog open={props.open} onOpenChange={handleOpenChange} dismissOnOutsideClick={props.dismissOnOutsideClick}>
			<DialogPortal>
				<DialogBackdrop />
				<DialogPopup showCloseButton={true} variant='default' styles={[s.popup]}>
					<AIAssistantDialogErrorBoundary>
						<AIAssistantDialogContent {...props} />
					</AIAssistantDialogErrorBoundary>
				</DialogPopup>
			</DialogPortal>
		</Dialog>
	);
};
